import { useState, useEffect, useRef, useCallback } from 'react';
import { useConversation } from '@elevenlabs/react';

interface UseComplianceAgentCallProps {
  agentId: string;
  systemPrompt: string;
  onTranscript?: (message: string, source: 'ai' | 'user') => void;
  onError?: (error: Error) => void;
}

export const useComplianceAgentCall = ({
  agentId,
  systemPrompt,
  onTranscript,
  onError,
}: UseComplianceAgentCallProps) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isAudioOn, setIsAudioOn] = useState(true);
  const startTimeRef = useRef<number>(Date.now());
  const transcriptRef = useRef<
    Array<{ source: 'ai' | 'user'; message: string }>
  >([]);

  const conversation = useConversation({
    agentId,
    connectionType: 'webrtc',
    overrides: {
      agent: {
        prompt: {
          prompt: systemPrompt,
        },
        firstMessage: 'Hello?',
      },
    },
    onConnect: () => {
      setIsConnected(true);
      console.log('Connected to compliance agent');
    },
    onDisconnect: () => {
      setIsConnected(false);
      console.log('Disconnected from compliance agent');
    },
    onMessage: (message) => {
      if (onTranscript) {
        const source = message.source === 'ai' ? 'ai' : 'user';
        onTranscript(message.message, source);
        transcriptRef.current.push({ source, message: message.message });
      }
    },
    onError: (err) => {
      console.error('Conversation error:', err);
      if (onError) {
        // Convert unknown error to Error object
        const error = err as any;
        onError(error);
      }
    },
  });

  const { status, isSpeaking } = conversation;

  const startCall = useCallback(async () => {
    try {
      await conversation.startSession({
        agentId,
        connectionType: 'webrtc',
      });
      startTimeRef.current = Date.now();
    } catch (err) {
      console.error('Failed to start call:', err);
      if (onError) {
        const error = err instanceof Error ? err : new Error(String(err));
        onError(error);
      }
    }
  }, [agentId, systemPrompt, conversation, onError]);

  const endCall = useCallback(() => {
    conversation.endSession();
    setIsConnected(false);
  }, [conversation]);

  const toggleAudio = useCallback(() => {
    if (isAudioOn) {
      conversation.setVolume({ volume: 0 });
    } else {
      conversation.setVolume({ volume: 1 });
    }
    setIsAudioOn(!isAudioOn);
  }, [conversation, isAudioOn]);

  // Auto-connect on mount
  useEffect(() => {
    if (status === 'connected') {
      setIsConnected(true);
    } else if (status === 'disconnected') {
      setIsConnected(false);
    }
  }, [status]);

  return {
    isConnected,
    isAudioOn,
    isSpeaking,
    startCall,
    endCall,
    toggleAudio,
    transcript: transcriptRef.current,
  };
};
